import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../config";
import { SEASON_EDITION_LABEL } from "../utils/jeedigunta";
import { FaArrowRight } from "react-icons/fa";

const Jeedigunta = () => {
  const [seasons, setSeasons] = useState([]);
  const [nomineeCounts, setNomineeCounts] = useState({}); // season_id → shortlist size
  const [honoreeCounts, setHonoreeCounts] = useState({}); // season_id → winner rows
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Jeedigunta Kathakala Puraskaralu";
  }, []);

  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: r }, { data: sl }] = await Promise.all([
        supabase.from("jeedigunta_seasons")
          .select("season_id, season_name, slug, status, date_from, date_to")
          .order("season_id", { ascending: false }),
        supabase.from("jeedigunta_results")
          .select("season_id"),
        supabase.from("jeedigunta_shortlist")
          .select("season_id"),
      ]);
      setSeasons(s || []);
      const honorees = {};
      (r || []).forEach((row) => {
        honorees[row.season_id] = (honorees[row.season_id] || 0) + 1;
      });
      setHonoreeCounts(honorees);
      const nominees = {};
      (sl || []).forEach((row) => {
        nominees[row.season_id] = (nominees[row.season_id] || 0) + 1;
      });
      setNomineeCounts(nominees);
      setLoading(false);
    })();
  }, []);

  const seasonsToShow = useMemo(
    () => (seasons || []).filter((s) => s.slug),
    [seasons]
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-kk-ink via-kk-maroon-deep to-kk-ink text-kk-cream">
      <div className="max-w-6xl mx-auto px-4 pt-4 pb-10 md:pt-6 md:pb-14">
        {/* Side-by-side: Jeedigunta hero logo on the left, title + season
            cards on the right. Stacks vertically on mobile. */}
        <div className="grid md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start">
          {/* ── LEFT: Hero logo ── */}
          <div className="flex justify-center md:justify-start md:sticky md:top-6">
            <img
              src="/logos/jeedigunta_puraskaralu_logo.jpg"
              alt="జీడిగుంట కథాకళ పురస్కారాలు · Jeedigunta Kathakala Puraskaralu"
              className="w-44 md:w-56 lg:w-64 h-auto rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.5)] border border-kk-gold/30"
            />
          </div>

          {/* ── RIGHT: title + description + season cards stacked tightly ── */}
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-kk-gold-light tracking-tight mb-1">
              జీడిగుంట కథాకళ పురస్కారాలు
            </h1>
            <p className="text-base md:text-lg text-kk-cream/85 italic font-serif">
              Jeedigunta Kathakala Puraskaralu
            </p>
            <p className="mt-3 md:mt-4 text-sm md:text-base text-kk-cream/70 max-w-2xl">
              Honouring excellence in Telugu storytelling — recognised by jury,
              peers, and audience across each annual cycle.
            </p>

            <h2 className="mt-6 md:mt-8 text-lg md:text-xl font-semibold text-kk-gold mb-4">
              Browse by Season
            </h2>

          {loading ? (
            <p className="text-kk-cream/60">Loading…</p>
          ) : seasonsToShow.length === 0 ? (
            <p className="text-kk-cream/60">No seasons yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {seasonsToShow.map((s) => {
                const ed = SEASON_EDITION_LABEL[s.slug] || { te: "", en: "" };
                const nominees = nomineeCounts[s.season_id] || 0;
                const honorees = honoreeCounts[s.season_id] || 0;
                return (
                  <Link
                    key={s.season_id}
                    to={`/jeedigunta/${s.slug}`}
                    className="group block p-6 bg-kk-ink/60 border border-kk-gold/20 rounded-2xl hover:border-kk-gold/60 hover:shadow-[0_0_24px_rgba(212,168,67,0.18)] transition-all"
                  >
                    <div className="flex items-baseline justify-between mb-3">
                      <span className="text-4xl md:text-5xl font-bold text-kk-gold-light">
                        {s.slug}
                      </span>
                      <FaArrowRight className="text-kk-gold/60 group-hover:text-kk-gold group-hover:translate-x-1 transition-all" />
                    </div>
                    <div className="text-base md:text-lg text-kk-cream font-medium">
                      {ed.te}
                    </div>
                    <div className="text-sm md:text-base text-kk-cream/70 italic mb-3">
                      {ed.en}
                    </div>
                    <div className="text-xs md:text-sm text-kk-gold/70 flex items-center gap-3 flex-wrap">
                      {nominees === 0 && honorees === 0 ? (
                        <span>Winners list pending</span>
                      ) : (
                        <>
                          {nominees > 0 && <span>{nominees} nominees</span>}
                          {nominees > 0 && honorees > 0 && (
                            <span className="opacity-50">·</span>
                          )}
                          {honorees > 0 && <span>{honorees} honorees</span>}
                        </>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
          </div>
        </div>

        {/* Footer / nav */}
        <footer className="mt-16 pt-8 border-t border-kk-gold/15 text-center text-sm text-kk-cream/60">
          <Link to="/" className="hover:text-kk-gold underline-offset-4 hover:underline">
            ← Back to Kathakala
          </Link>
          <span className="mx-3">·</span>
          <Link to="/utsavam" className="hover:text-kk-gold underline-offset-4 hover:underline">
            Utsavam Sessions
          </Link>
        </footer>
      </div>
    </div>
  );
};

export default Jeedigunta;
